import React, { useState, useEffect } from "react";
import { Tag, X } from "lucide-react";
import { toast } from "sonner";

export const ClassifyModal = ({ isOpen, onClose, onSubmit, transaction = null, bankAccounts = [], costCenters = [] }) => {
  const [bankAccountId, setBankAccountId] = useState("");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [costCenterId, setCostCenterId] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (isOpen && transaction) {
      setBankAccountId(transaction.bank_account_id || "");
      setCategory(transaction.category === "A classificar" ? "" : transaction.category || "");
      setSubcategory(transaction.subcategory || "");
      setCostCenterId(transaction.cost_center_id || "");
      setDescription(transaction.description || "");
    }
  }, [isOpen, transaction]);

  if (!isOpen || !transaction) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!bankAccountId) {
      toast.error("Escolha uma conta bancária.");
      return;
    }
    if (!category.trim()) {
      toast.error("Informe a categoria.");
      return;
    }
    onSubmit({
      bank_account_id: bankAccountId,
      category: category.trim(),
      subcategory: subcategory.trim() || null,
      cost_center_id: costCenterId || null,
      description: description.trim(),
    });
  };

  const formatCurrency = (n) =>
    (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <div
        data-testid="modal-classify-form"
        className="w-full max-w-lg bg-[#131C2E] border border-[#D4AF37]/40 rounded-2xl shadow-[0_0_50px_rgba(212,175,55,0.25)] overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-[#080C14]">
          <div className="flex items-center space-x-2">
            <Tag className="w-5 h-5 text-[#D4AF37]" />
            <h3 className="font-serif font-bold text-lg text-white">Classificar Lançamento</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
            data-testid="btn-close-classify-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs text-slate-300 max-h-[75vh] overflow-y-auto">

          <div className="p-3 rounded-xl bg-[#080C14] border border-slate-700">
            <p className="text-[11px] text-slate-400 mb-1 uppercase tracking-widest">Lançamento</p>
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-white truncate">{transaction.description}</p>
              <span
                className={`font-mono font-bold text-sm ${transaction.type === "receita" ? "text-emerald-400" : "text-rose-400"}`}
              >
                {transaction.type === "receita" ? "+" : "-"} {formatCurrency(transaction.amount)}
              </span>
            </div>
          </div>

          <div>
            <label className="block mb-1 font-semibold text-slate-300">Conta Bancária *</label>
            <select
              data-testid="select-classify-bank"
              value={bankAccountId}
              onChange={(e) => setBankAccountId(e.target.value)}
              required
              className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
            >
              <option value="">— Escolha uma conta —</option>
              {bankAccounts.filter(b => b.is_active !== false).map((ba) => (
                <option key={ba.id} value={ba.id}>
                  {ba.name}{ba.bank_name ? ` (${ba.bank_name})` : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block mb-1 font-semibold text-slate-300">Categoria *</label>
              <input
                data-testid="input-classify-category"
                type="text"
                required
                placeholder="Ex: Doação, Mensalidade, Aluguel..."
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              />
            </div>
            <div>
              <label className="block mb-1 font-semibold text-slate-300">Subcategoria</label>
              <input
                data-testid="input-classify-subcategory"
                type="text"
                placeholder="Opcional"
                value={subcategory}
                onChange={(e) => setSubcategory(e.target.value)}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              />
            </div>
          </div>

          {costCenters.length > 0 && (
            <div>
              <label className="block mb-1 font-semibold text-slate-300">Centro de Custo</label>
              <select
                data-testid="select-classify-cost-center"
                value={costCenterId}
                onChange={(e) => setCostCenterId(e.target.value)}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              >
                <option value="">— Nenhum —</option>
                {costCenters.map((cc) => (
                  <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block mb-1 font-semibold text-slate-300">Descrição Final</label>
            <input
              data-testid="input-classify-description"
              type="text"
              placeholder="Descrição atualizada"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
            />
          </div>

          <div className="pt-4 border-t border-slate-800 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300 hover:text-white font-semibold text-xs"
            >
              Cancelar
            </button>
            <button
              data-testid="btn-submit-classify"
              type="submit"
              className="bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-6 py-2 rounded-xl text-xs uppercase shadow-[0_0_15px_rgba(212,175,55,0.3)]"
            >
              Classificar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
